/**
 * ColorModeContext
 *
 * Provides app-wide light/dark mode with system-preference fallback and
 * localStorage persistence. Wraps the MUI ThemeProvider so any component
 * can call `useTheme()` and read palette.mode without prop-drilling.
 *
 * Resolution order on first load:
 *   1. localStorage("ajo.colorMode") — explicit user choice, sticky
 *   2. window.matchMedia('(prefers-color-scheme: dark)') — OS preference
 *   3. dark — historical default for this app
 *
 * Switching is instant (CssBaseline + theme transitions handle the
 * background/text fade).
 */
import * as React from 'react';
import { ThemeProvider, useTheme as useMuiTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
// @adobe/react-spectrum was removed from the runtime — its ESM bundle
// resolves `react-aria/VisuallyHidden` which Vite's commonjs resolver
// can't pick up under the project's current package version. The
// SpectrumProvider was only wrapping children with a theme + locale;
// no Spectrum widgets remain in the tree, so this passthrough is
// behaviour-preserving.
const SpectrumProvider: React.FC<{ children?: React.ReactNode } & Record<string, unknown>> = ({ children }) => <>{children}</>;
// @ts-expect-error JS module
import { createAppTheme } from './theme';

type Mode = 'light' | 'dark';

interface ColorModeContextValue {
  mode: Mode;
  toggle: () => void;
  setMode: (m: Mode) => void;
}

const STORAGE_KEY = 'ajo.colorMode';

const ColorModeContext = React.createContext<ColorModeContextValue>({
  mode: 'dark',
  toggle: () => {},
  setMode: () => {},
});

function resolveInitialMode(): Mode {
  if (typeof window === 'undefined') return 'dark';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage may throw in private mode; fall through.
  }
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

export function ColorModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = React.useState<Mode>(resolveInitialMode);

  // Re-emit OS-preference changes only if the user hasn't explicitly chosen.
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return; // user-locked
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => {
      setModeState(e.matches ? 'dark' : 'light');
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const setMode = React.useCallback((next: Mode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // best-effort
    }
  }, []);

  const toggle = React.useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setMode]);

  const theme = React.useMemo(() => createAppTheme({ darkMode: mode === 'dark' }), [mode]);

  const value = React.useMemo<ColorModeContextValue>(
    () => ({ mode, toggle, setMode }),
    [mode, toggle, setMode],
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/*
          React Spectrum sits inside the MUI ThemeProvider so future
          Spectrum components inherit the same dark/light toggle without
          a second context plumbing. Existing MUI components are
          unaffected — the two libraries coexist; Spectrum's <Provider>
          only styles its own descendants.

          UNSAFE_style with width/height: 100% prevents Spectrum from
          shrink-wrapping the app shell to its widget content.
        */}
        <SpectrumProvider>
          {children}
        </SpectrumProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export function useColorMode() {
  return React.useContext(ColorModeContext);
}

// Convenience: re-export MUI's useTheme so consumers can grab palette tokens.
export { useMuiTheme };
